

import { useState, useEffect } from 'react';
import { apiRequest } from '../../fetchSetup';


/**
 * Custom hook for handling Razorpay payments
 * @param {Object} config - Configuration object
 * @param {string} config.userEmail - User's email
 * @param {string} config.userName - User's name
 * @param {string} config.userPhone - User's phone
 * @param {Function} config.onSuccess - Callback when payment succeeds
 * @param {Function} config.onFailure - Callback when payment fails
 * @returns {Object} Payment handler methods and state
 */
export const usePaymentHandler = (config) => {
  const { userEmail, userName, userPhone, onSuccess, onFailure } = config;
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load Razorpay script on mount
  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        if (window.Razorpay) {
          setRazorpayLoaded(true);
          resolve(true);
          return;
        }

        const script = document.createElement('script');
        script.src = process.env.REACT_APP_RAZORPAY_SCRIPT;
        script.async = true;
        
        script.onload = () => {
          console.log('✅ Razorpay script loaded successfully');
          setRazorpayLoaded(true);
          resolve(true);
        };
        
        script.onerror = () => {
          console.error('❌ Failed to load Razorpay script');
          resolve(false);
        };
        
        document.body.appendChild(script);
      });
    };

    loadRazorpayScript();
  }, []);

  /**
   * Process offline payment (Cash on Delivery)
   * @param {number} amount - Payment amount
   * @param {Object} metadata - Additional metadata
   * @returns {Object} Payment data
   */
  const processOfflinePayment = async (amount, metadata = {}) => {
    console.log('💵 Processing cash on delivery...');

    const paymentData = {
      payment_id: `offline_${Date.now()}`,
      order_id: null,
      method: 'offline',
      status: 'pending',
      amount: amount,
      gateway: null,
      currency: 'INR',
      transaction_history: [
        {
          timestamp: new Date().toISOString(),
          method: 'offline',
          status: 'pending',
        },
      ],
      ...metadata,
    };

    return paymentData;
  };

  /**
   * Process online payment via Razorpay
   * @param {Object} params - Payment parameters
   * @param {number} params.amount - Payment amount
   * @param {string} params.orderType - Type of order
   * @param {string} params.description - Payment description
   * @param {Object} params.metadata - Additional metadata
   * @returns {Promise} Payment result
   */
  const processOnlinePayment = async ({ amount, orderType, description, metadata = {} }) => {
    if (!razorpayLoaded || !window.Razorpay) {
      throw new Error('Payment gateway is loading. Please wait a moment and try again.');
    }

    if (!userEmail) {
      throw new Error('User information is missing');
    }

    try {
      setLoading(true);
      console.log('💳 Starting online payment process...');
      console.log('Amount (before conversion):', amount);

      // Convert amount to paise (smallest currency unit) - Razorpay expects amount in paise
      // const amountInPaise = Math.round(amount * 100);
      // console.log('Amount in paise:', amountInPaise);

      // Create Razorpay order
      const orderRes = await apiRequest(process.env.REACT_APP_CREATE_RAZORPAY_ORDER, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amount, 
          currency: 'INR',
          receipt: `${orderType}_${Date.now()}`,
          notes: {
            order_type: orderType,
            customer_email: userEmail,
            ...metadata,
          },
        }),
      });

      const responseText = await orderRes.text();
      console.log('Raw Razorpay response:', responseText);

      let orderData;
      try {
        orderData = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse Razorpay response:', responseText);
        throw new Error('Invalid response from payment gateway');
      }

      if (!orderRes.ok) {
        console.error('❌ Razorpay order creation failed:', orderData);
        const errorMsg = orderData.error || orderData.detail || orderData.message || 'Failed to create payment order';
        throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      }

      console.log('✅ Razorpay order created:', orderData);

      // Return promise that resolves when payment completes
      return new Promise((resolve, reject) => {
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY || "rzp_test_RLPNXraO5Rywls",
          amount: orderData.amount,
          currency: orderData.currency,
          name: 'Ayuja Medicines',
          description: description,
          order_id: orderData.id,
          prefill: {
            name: userName,
            email: userEmail,
            contact: userPhone,
          },
          theme: { color: '#006D77' },
          handler: async function (paymentSuccess) {
            console.log('💳 Payment Success:', paymentSuccess);
            
            try {
              // Verify payment
              const verifyRes = await apiRequest(process.env.REACT_APP_PROCESS_PAYEMENT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  razorpay_payment_id: paymentSuccess.razorpay_payment_id,
                  razorpay_order_id: paymentSuccess.razorpay_order_id,
                  razorpay_signature: paymentSuccess.razorpay_signature,
                  method: 'online',
                  amount: orderData.amount,
                }),
              });

              const paymentResult = await verifyRes.json();
              console.log('🔍 Payment verification:', paymentResult);

              if (paymentResult.status !== 'success') {
                throw new Error('Payment verification failed');
              }

              const onlinePaymentData = {
                payment_id: paymentSuccess.razorpay_payment_id,
                order_id: paymentSuccess.razorpay_order_id,
                method: 'online',
                status: 'success',
                amount: orderData.amount,
                gateway: 'razorpay',
                currency: 'INR',
                transaction_history: [
                  {
                    timestamp: new Date().toISOString(),
                    method: 'online',
                    gateway: 'razorpay',
                    payment_id: paymentSuccess.razorpay_payment_id,
                    order_id: paymentSuccess.razorpay_order_id,
                    status: 'success',
                  },
                ],
              };

              setLoading(false);
              resolve(onlinePaymentData);
              
              if (onSuccess) {
                onSuccess(onlinePaymentData);
              }
            } catch (error) {
              console.error('💥 Post-payment error:', error);
              setLoading(false);
              reject(error);
              
              if (onFailure) {
                onFailure(error, paymentSuccess.razorpay_payment_id);
              }
            }
          },
          modal: {
            ondismiss: function() {
              console.log('❌ Payment cancelled');
              setLoading(false);
              reject(new Error('Payment cancelled by user'));
            }
          }
        };

        const rzp = new window.Razorpay(options);
        
        rzp.on('payment.failed', function (response) {
          console.log('❌ Payment failed:', response.error);
          setLoading(false);
          const error = new Error(response.error.description || response.error.message || 'Payment failed');
          reject(error);
          
          if (onFailure) {
            onFailure(error, null);
          }
        });

        rzp.open();
        setLoading(false);
      });

    } catch (error) {
      console.error('💥 Payment initialization error:', error);
      setLoading(false);
      throw error;
    }
  };

  return {
    razorpayLoaded,
    loading,
    processOfflinePayment,
    processOnlinePayment,
  };
};

/**
 * Create medicine order
 * @param {Object} orderPayload - Order payload
 * @returns {Promise} Order creation result
 */
export const createMedicineOrder = async (orderPayload) => {
  try {
    console.log('📝 Creating medicine order with payload:', JSON.stringify(orderPayload, null, 2));

    const response = await apiRequest(process.env.REACT_APP_CREATE_MEDICINE_ORDER, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderPayload),
    });

    const responseText = await response.text();
    console.log('📥 Raw server response:', responseText);

    let result;
    try {
      result = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse response:', responseText);
      throw new Error('Invalid server response');
    }

    if (!response.ok) {
      console.error('❌ Order creation failed:', result);
      throw new Error(result.error || `Server error: ${response.status}`);
    }

    console.log('✅ Order created successfully:', result);
    return result;

  } catch (error) {
    console.error('💥 Order creation error:', error);
    throw error;
  }
};

/**
 * Update payment status for existing order
 * @param {string} orderId - Order ID
 * @param {Object} paymentData - Payment data
 * @returns {Promise} Update result
 */
export const updateOrderPayment = async (orderId, paymentData) => {
  try {
    console.log('📝 Updating payment for order:', orderId);
    console.log('Payment data:', paymentData);

    // Construct the correct endpoint URL - FIXED PATH
    const endpoint = `${process.env.REACT_APP_CREATE_MEDICINE_ORDER}/${orderId}/payment`;
    console.log('PUT endpoint:', endpoint);

    const response = await apiRequest(endpoint, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(paymentData),
    });

    const responseText = await response.text();
    console.log('Raw payment update response:', responseText);

    let result;
    try {
      result = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse payment update response:', responseText);
      throw new Error('Invalid server response');
    }

    if (!response.ok) {
      console.error('❌ Payment update failed:', result);
      const errorMsg = result.error || result.detail || result.message || 'Failed to update payment';
      throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }

    console.log('✅ Payment updated successfully:', result);
    return result;

  } catch (error) {
    console.error('💥 Payment update error:', error);
    throw error;
  }
};